<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold">Gestão de Usuários</h1>
      <button
        @click="openUserDialog()"
        class="bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-dark/90"
      >
        Adicionar Usuário
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <i class="pi pi-spin pi-spinner text-2xl text-primary-dark"></i>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8 text-red-600">
      {{ error }}
    </div>
    
    <!-- Users Table -->
    <div v-else class="bg-white rounded-lg shadow overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Perfil</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="user in users" :key="user.id">
            <td class="px-6 py-4 whitespace-nowrap">{{ user.name }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ user.email }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ user.document }}</td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <i 
                  v-if="getProfile(user.profileId)?.icon"
                  :class="['pi text-lg', getProfile(user.profileId)?.icon]" 
                  :style="{ color: getProfile(user.profileId)?.color }"
                ></i>
                <span>{{ getProfile(user.profileId)?.name }}</span>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
              <button 
                @click="openUserDialog(user)"
                class="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-full transition-colors"
                title="Editar usuário"
              >
                <i class="pi pi-pencil"></i>
              </button>
              <button 
                v-if="user.active"
                @click="confirmBlock(user)"
                class="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-full transition-colors"
                title="Bloquear usuário"
              >
                <i class="pi pi-lock"></i>
              </button>
              <button 
                v-else
                @click="confirmUnblock(user)"
                class="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-full transition-colors"
                title="Desbloquear usuário"
              >
                <i class="pi pi-lock-open"></i>
              </button>
              <button 
                @click="confirmDelete(user)"
                class="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-full transition-colors"
                title="Excluir usuário"
              >
                <i class="pi pi-trash"></i>
              </button>
            </td>
          </tr>
          <tr v-if="users.length === 0">
            <td colspan="5" class="px-6 py-4 text-center text-gray-500">
              Nenhum usuário encontrado
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal de Usuário -->
    <Dialog
      v-model:visible="userDialogVisible"
      :modal="true"
      class="p-fluid"
      :style="{ width: '550px' }"
      :closable="false"
    >
      <template #header>
        <div class="flex items-center gap-3 px-4 py-2 bg-gray-50 -mx-4 -mt-4 rounded-t-lg border-b">
          <i :class="['pi text-2xl', selectedUser ? 'pi-user-edit text-blue-500' : 'pi-user-plus text-green-500']"></i>
          <div>
            <h2 class="text-xl font-semibold text-gray-800">{{ selectedUser ? 'Editar' : 'Novo' }} Usuário</h2>
            <p class="text-sm text-gray-600 mt-1">
              {{ selectedUser ? 'Atualize as informações do usuário' : 'Preencha as informações para criar um novo usuário' }}
            </p>
          </div>
        </div>
      </template>

      <form @submit.prevent="handleSubmit" class="px-1">
        <div class="grid gap-4">
          <!-- Email -->
          <div class="col-12">
            <span class="p-float-label">
              <InputText
                id="email"
                v-model="userForm.email"
                :class="{ 'w-full': true, 'p-invalid': v$.email.$invalid && v$.email.$dirty }"
                :disabled="!!selectedUser"
              />
              <label for="email">Email</label>
            </span>
            <small v-if="v$.email.$invalid && v$.email.$dirty" class="p-error">
              Email é obrigatório e deve ser válido
            </small>
          </div>

          <!-- Nome -->
          <div class="col-12">
            <span class="p-float-label">
              <InputText
                id="name"
                v-model="userForm.name"
                :class="{ 'w-full': true, 'p-invalid': v$.name.$invalid && v$.name.$dirty }"
              />
              <label for="name">Nome</label>
            </span>
            <small v-if="v$.name.$invalid && v$.name.$dirty" class="p-error">
              Nome é obrigatório
            </small>
          </div>

          <!-- Documento -->
          <div class="col-12">
            <span class="p-float-label">
              <InputText
                id="document"
                v-model="userForm.document"
                :class="{ 'w-full': true, 'p-invalid': v$.document.$invalid && v$.document.$dirty }"
              />
              <label for="document">Documento (CPF/CNPJ)</label>
            </span>
            <small v-if="v$.document.$invalid && v$.document.$dirty" class="p-error">
              Documento é obrigatório
            </small>
          </div>

          <!-- Perfil -->
          <div class="col-12">
            <span class="p-float-label">
              <Dropdown
                v-model="userForm.profileId"
                :options="profileStore.profiles"
                optionLabel="name"
                optionValue="id"
                :class="{ 'w-full': true, 'p-invalid': v$.profileId.$invalid && v$.profileId.$dirty }"
              >
                <template #value="slotProps">
                  <div v-if="slotProps.value" class="flex items-center gap-2">
                    <i 
                      v-if="getProfile(slotProps.value)?.icon"
                      :class="['pi text-lg', getProfile(slotProps.value)?.icon]" 
                      :style="{ color: getProfile(slotProps.value)?.color }"
                    ></i>
                    <span>{{ getProfile(slotProps.value)?.name }}</span>
                  </div>
                  <span v-else>Selecione um perfil</span>
                </template>
                <template #option="slotProps">
                  <div class="flex items-center gap-2">
                    <i 
                      v-if="slotProps.option.icon"
                      :class="['pi text-lg', slotProps.option.icon]" 
                      :style="{ color: slotProps.option.color }"
                    ></i>
                    <span>{{ slotProps.option.name }}</span>
                  </div>
                </template>
              </Dropdown>
              <label for="profile">Perfil</label>
            </span>
            <small v-if="v$.profileId.$invalid && v$.profileId.$dirty" class="p-error">
              Perfil é obrigatório
            </small>
          </div>

<!-- Organizações -->
<div class="col-12">
  <span class="p-float-label">
    <MultiSelect
      v-model="selectedOrganizations"
      :options="organizationStore.organizations"
      optionLabel="name"
      :class="{ 'w-full': true, 'p-invalid': !selectedOrganizations.length }"
      display="chip"
    >
      <template #value="slotProps">
        <div v-if="slotProps.value && slotProps.value.length" class="flex flex-wrap gap-1">
          <div v-for="org in slotProps.value" :key="org.id" class="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm flex items-center gap-1">
            <i class="pi pi-building text-xs"></i>
            <span>{{ org.name }}</span>
          </div>
        </div>
        <span v-else>Selecione as organizações</span>
      </template>
      <template #option="slotProps">
        <div class="flex items-center gap-2">
          <i class="pi pi-building"></i>
          <span>{{ slotProps.option.name }}</span>
        </div>
      </template>
    </MultiSelect>
    <label for="organizations">Organizações</label>
  </span>
  <small v-if="!selectedOrganizations.length" class="p-error">
    Selecione pelo menos uma organização
  </small>
</div>

          <!-- Senha gerada para novo usuário -->
          <div v-if="!selectedUser && generatedPassword" class="col-12">
            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-2">
                <i class="pi pi-key text-green-600"></i>
                <span class="font-semibold text-green-700">Senha inicial gerada</span>
              </div>
              <p class="text-green-600 mb-2">
                A seguinte senha foi gerada para o usuário. Certifique-se de compartilhá-la de forma segura:
              </p>
              <div class="bg-white p-2 rounded border border-green-200 font-mono text-green-700">
                {{ generatedPassword }}
              </div>
            </div>
          </div>
        </div>
      </form>

      <template #footer>
        <div class="flex justify-end gap-2 px-4 py-3 bg-gray-50 -mx-4 -mb-4 border-t">
          <Button
            type="button"
            label="Cancelar"
            icon="pi pi-times"
            text
            @click="closeDialog"
            class="px-4"
          />
          <Button
            type="button"
            :label="selectedUser ? 'Salvar' : 'Criar Usuário'"
            :icon="selectedUser ? 'pi pi-check' : 'pi pi-user-plus'"
            :loading="loading"
            @click="handleSubmit"
            severity="primary"
            class="px-4"
          />
        </div>
      </template>
    </Dialog>

    <!-- Modal de confirmação de bloqueio -->
    <Dialog
      v-model:visible="showConfirmBlock"
      modal
      header="Confirmar Bloqueio"
      :style="{ width: '350px' }"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
        <span>Tem certeza que deseja bloquear este usuário?</span>
      </div>
      <template #footer>
        <Button
          label="Não"
          icon="pi pi-times"
          @click="showConfirmBlock = false"
          class="p-button-text"
        />
        <Button
          label="Sim"
          icon="pi pi-check"
          @click="blockUser(userToBlock)"
          class="p-button-danger"
          :loading="loading"
        />
      </template>
    </Dialog>

    <!-- Modal de confirmação de desbloqueio -->
    <Dialog
      v-model:visible="showConfirmUnblock"
      modal
      header="Confirmar Desbloqueio"
      :style="{ width: '350px' }"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
        <span>Tem certeza que deseja desbloquear este usuário?</span>
      </div>
      <template #footer>
        <Button
          label="Não"
          icon="pi pi-times"
          @click="showConfirmUnblock = false"
          class="p-button-text"
        />
        <Button
          label="Sim"
          icon="pi pi-check"
          @click="unblockUser(userToUnblock)"
          class="p-button-success"
          :loading="loading"
        />
      </template>
    </Dialog>

    <!-- Modal de confirmação de exclusão -->
    <Dialog
      v-model:visible="showDeleteConfirm"
      modal
      header="Confirmar Exclusão"
      :style="{ width: '350px' }"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
        <span>Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.</span>
      </div>
      <template #footer>
        <Button
          label="Não"
          icon="pi pi-times"
          @click="showDeleteConfirm = false"
          class="p-button-text"
        />
        <Button
          label="Sim"
          icon="pi pi-check"
          @click="deleteUser(userToDelete)"
          class="p-button-danger"
          :loading="loading"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue'
import { useUserStore } from '@/stores/user'
import { useProfileStore } from '@/stores/profile'
import Dialog from 'primevue/dialog'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import MultiSelect from 'primevue/multiselect'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import { useOrganizationStore } from '@/stores/organization'
import { userOrganizationService } from '@/firebase/services/userOrganization.service'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { doc, updateDoc, collection, getDocs, deleteDoc } from 'firebase/firestore'
import { db } from '@/firebase/config'
import { createUser } from '@/firebase/services/auth.service'

const toast = useToast()
const userStore = useUserStore()
const profileStore = useProfileStore()
const organizationStore = useOrganizationStore()

const userDialogVisible = ref(false)
const showConfirmBlock = ref(false)
const showConfirmUnblock = ref(false)
const showDeleteConfirm = ref(false)
const userToBlock = ref<any>(null)
const userToUnblock = ref<any>(null)
const userToDelete = ref<any>(null)
const selectedUser = ref<any>(null)
const selectedOrganizations = ref([])
const generatedPassword = ref('')
const loading = ref(false)
const users = ref([])
const error = ref(null)

// Carregar usuários
const loadUsers = async () => {
  try {
    loading.value = true
    const usersSnapshot = await getDocs(collection(db, 'users'))
    const usersData = []
    
    for (const doc of usersSnapshot.docs) {
      const userData = { id: doc.id, ...doc.data() }
      // Carregar organizações do usuário
      const userOrgs = await userOrganizationService.getUserOrganizations(doc.id)
      userData.organizations = userOrgs.map(org => org.organizationId)
      usersData.push(userData)
    }
    
    users.value = usersData.filter(user => !user.deletedAt)
  } catch (error) {
    console.error('Erro ao carregar usuários:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao carregar usuários',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

// Carregar organizações do usuário
const loadUserOrganizations = async (userId: string) => {
  try {
    const orgs = await userOrganizationService.getUserOrganizations(userId)
    // Mapear as organizações para o formato esperado pelo MultiSelect
    const userOrgs = await Promise.all(
      orgs.map(async (org) => {
        const orgData = organizationStore.organizations.find(o => o.id === org.organizationId)
        return orgData
      })
    )
    selectedOrganizations.value = userOrgs.filter(org => org) // Remover undefined
  } catch (error) {
    console.error('Erro ao carregar organizações do usuário:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao carregar organizações do usuário',
      life: 3000
    })
  }
}

const userForm = reactive({
  id: '',
  email: '',
  name: '',
  profileId: '',
  document: ''
})

const rules = {
  email: { required, email },
  name: { required },
  profileId: { required },
  document: { required }
}

const v$ = useVuelidate(rules, userForm)

const resetForm = () => {
  userForm.id = ''
  userForm.email = ''
  userForm.name = ''
  userForm.profileId = ''
  userForm.document = ''
  generatedPassword.value = ''
  v$.value.$reset()
}

const copyPassword = () => {
  navigator.clipboard.writeText(generatedPassword.value)
  toast.add({
    severity: 'success',
    summary: 'Senha copiada',
    detail: 'A senha foi copiada para a área de transferência',
    life: 3000
  })
}

const openUserDialog = (user = null) => {
  selectedUser.value = user
  if (user) {
    userForm.id = user.id
    userForm.email = user.email
    userForm.name = user.name
    userForm.profileId = user.profileId
    userForm.document = user.document
    loadUserOrganizations(user.id)
  } else {
    resetForm()
    generatedPassword.value = generateRandomPassword()
  }
  userDialogVisible.value = true
}

const closeDialog = () => {
  userDialogVisible.value = false
  resetForm()
}

const handleSubmit = async () => {
  const isValid = await v$.value.$validate()
  if (!isValid) {
    return
  }
  await saveUser()
}

const saveUser = async () => {
  try {
    loading.value = true
    if (!selectedUser.value) {
      // Validar se tem organização e perfil selecionados
      if (!selectedOrganizations.value || selectedOrganizations.value.length === 0) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Selecione pelo menos uma organização',
          life: 3000
        })
        return
      }

      if (!userForm.profileId) {
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Selecione um perfil',
          life: 3000
        })
        return
      }

      // Criar novo usuário
      const userData = {
        email: userForm.email,
        name: userForm.name,
        profileId: userForm.profileId,
        document: userForm.document,
        role: 'user',
        organizations: selectedOrganizations.value.map(org => org.id)
      }
      
      // Criar usuário no Firebase Authentication e Firestore
      try {
        const userCredential = await createUser(userData, generatedPassword.value)
        
        // Atualizar relações usuário-organização
        await userOrganizationService.updateUserOrganizations(
          userCredential.user.uid,
          selectedOrganizations.value.map(org => org.id)
        )

        toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `Usuário criado com sucesso! A senha inicial é: ${generatedPassword.value}`,
          life: 10000
        })
      } catch (error: any) {
        if (error.code === 'auth/email-already-in-use') {
          toast.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Este e-mail já está em uso. Por favor, use outro e-mail.',
            life: 5000
          })
          return
        }
        throw error
      }
    } else {
      // Atualizar usuário existente
      const updates = {
        name: userForm.name,
        profileId: userForm.profileId,
        document: userForm.document,
        updatedAt: new Date().toISOString(),
        organizations: selectedOrganizations.value.map(org => org.id)
      }

      // Atualizar documento do usuário
      await updateDoc(doc(db, 'users', userForm.id), updates)
      
      // Atualizar relações usuário-organização
      await userOrganizationService.updateUserOrganizations(
        userForm.id,
        selectedOrganizations.value.map(org => org.id)
      )

      toast.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Usuário atualizado com sucesso',
        life: 3000
      })
    }
    
    closeDialog()
    loadUsers()
  } catch (error: any) {
    console.error('Erro ao salvar usuário:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: error.message || 'Erro ao salvar usuário',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const blockUser = async (user) => {
  try {
    loading.value = true
    await updateDoc(doc(db, 'users', user.id), {
      active: false,
      updatedAt: new Date().toISOString()
    })
    
    // Atualizar o usuário na lista local
    const index = users.value.findIndex(u => u.id === user.id)
    if (index !== -1) {
      users.value[index].active = false
    }
    
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Usuário bloqueado com sucesso',
      life: 3000
    })
    
    showConfirmBlock.value = false
  } catch (error) {
    console.error('Erro ao bloquear usuário:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao bloquear usuário',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const unblockUser = async (user) => {
  try {
    loading.value = true
    await updateDoc(doc(db, 'users', user.id), {
      active: true,
      updatedAt: new Date().toISOString()
    })
    
    // Atualizar o usuário na lista local
    const index = users.value.findIndex(u => u.id === user.id)
    if (index !== -1) {
      users.value[index].active = true
    }
    
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Usuário desbloqueado com sucesso',
      life: 3000
    })
    
    showConfirmUnblock.value = false
  } catch (error) {
    console.error('Erro ao desbloquear usuário:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao desbloquear usuário',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const deleteUser = async (user) => {
  try {
    loading.value = true
    
    // Primeiro, remover todas as associações com organizações
    const userOrgs = await userOrganizationService.getUserOrganizations(user.id)
    await Promise.all(
      userOrgs.map(org => userOrganizationService.removeUserFromOrganization(org.id))
    )
    
    // Depois, excluir o usuário
    await updateDoc(doc(db, 'users', user.id), {
      deletedAt: new Date().toISOString()
    })
    
    // Remover o usuário da lista local
    users.value = users.value.filter(u => u.id !== user.id)
    
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Usuário excluído com sucesso',
      life: 3000
    })
    
    showDeleteConfirm.value = false
  } catch (error) {
    console.error('Erro ao excluir usuário:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao excluir usuário',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const confirmBlock = (user) => {
  userToBlock.value = user
  showConfirmBlock.value = true
}

const confirmUnblock = (user) => {
  userToUnblock.value = user
  showConfirmUnblock.value = true
}

const confirmDelete = (user) => {
  userToDelete.value = user
  showDeleteConfirm.value = true
}

const getProfile = (profileId: string) => {
  return profileStore.profiles.find(p => p.id === profileId)
}

onMounted(async () => {
  await Promise.all([
    loadUsers(),
    profileStore.fetchProfiles(),
    organizationStore.fetchOrganizations()
  ])
})

const generateRandomPassword = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+';
  const passwordLength = 12;
  let password = '';
  for (let i = 0; i <passwordLength; i++) {
    password += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return password;
}
</script>